import PrivacyPolicyDialog from '@/components/PageSpecific/privacy-policy/PrivacyPolicyDialog';
import TermsOfUseDialog from '@/components/PageSpecific/terms-of-use/TermsOfUseDialog';
import ErrorBoundary from '@/components/Reusable/ErrorHandling/ErrorBoundary';
import GoGovSidebarItems from '@/go.gov/components/layout/SidebarItems';
import Logo from '@/ui/Layout/Logo';
import ContactUsModal from '@/ui/Layout/Sidebar/ContactUsModal';
import CustomerSupportModal from '@/ui/Layout/Sidebar/CustomerSupportModal';
import FooterSection from '@/ui/Layout/Sidebar/FooterSection';
import { NavigationItem } from '@/ui/Layout/Sidebar/SidebarItem';
import SidebarItems from '@/ui/Layout/Sidebar/SidebarItems';
import { IS_ETS_NEXT } from '@/utils/constants';
import useSession from '@/utils/hooks/useSession';
import {
  effectiveShowContactUs,
  effectiveShowGiveFeedback,
  effectiveShowReleaseNotes,
  getContactUsUrlValidationError,
  parseWhiteLabelingConfig,
  persistContactUsUrl,
  resolveContactUsCustomHtmlFromOrg,
} from '@/utils/whiteLabeling/config';
import { Box } from '@mui/material';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface SidebarBaseProps {
  collapsed: boolean;
  onExpandSidebar?: () => void;
}

const SidebarBase: React.FC<SidebarBaseProps> = ({
  collapsed,
  onExpandSidebar,
}) => {
  const router = useRouter();
  const { org } = useSession();

  const [customerSupportModalOpen, setCustomerSupportModalOpen] =
    useState(false);
  const [contactUSModalOpen, setContactUSModalOpen] = useState(false);
  const [openTOU, setOpenTOU] = useState(false);
  const [openPP, setOpenPP] = useState(false);

  const openCustomerSupportModal = () => setCustomerSupportModalOpen(true);
  const openTermsOfUse = () => setOpenTOU(true);
  const openPrivacyPolicy = () => setOpenPP(true);

  const focusMainContent = useCallback(() => {
    requestAnimationFrame(() => {
      document.getElementById('main-content')?.focus();
    });
  }, []);

  const handleContactUsClick = useCallback(() => {
    if (resolveContactUsCustomHtmlFromOrg(org)) {
      setContactUSModalOpen(true);
      return;
    }

    const url = persistContactUsUrl(
      parseWhiteLabelingConfig(org?.whiteLabelingConfig)?.contactUsUrl
    );
    if (url && !getContactUsUrlValidationError(url)) {
      if (url.startsWith('/')) {
        void router.push(url);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    setContactUSModalOpen(true);
  }, [org, router]);

  const handleItemClick = useCallback(
    async (item: NavigationItem) => {
      if (item.url && !item.external) {
        await router.push(item.url);
        focusMainContent();
      }
    },
    [focusMainContent, router]
  );

  const logoRef = useRef<HTMLDivElement>(null);
  const [logoHeight, setLogoHeight] = useState<number>(48);
  useEffect(() => {
    if (logoRef.current) {
      setLogoHeight(logoRef.current.offsetHeight || 48);
    }
  }, [collapsed]);

  const currentPath = router.asPath;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'space-between',
        }}
      >
        {/* Logo Section */}
        <Box
          ref={logoRef}
          sx={{
            p: collapsed ? 1 : 2,
            flexShrink: 0,
            minHeight: logoHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'min-height 0.2s',
          }}
        >
          {!collapsed && !IS_ETS_NEXT && <Logo />}
        </Box>

        {/* Navigation Items */}
        {IS_ETS_NEXT ? (
          <GoGovSidebarItems
            collapsed={collapsed}
            currentPath={currentPath}
            handleItemClick={handleItemClick}
            onExpandSidebar={onExpandSidebar}
          />
        ) : (
          <SidebarItems
            collapsed={collapsed}
            currentPath={currentPath}
            handleItemClick={handleItemClick}
            onExpandSidebar={onExpandSidebar}
          />
        )}

        {/* Footer Section */}
        {!IS_ETS_NEXT && (
          <FooterSection
            collapsed={collapsed}
            onContactUs={handleContactUsClick}
            onReleaseNotes={() =>
              handleItemClick({
                label: 'Release Notes',
                url: '/personal-settings/hub-releases',
              })
            }
            onGiveFeedback={openCustomerSupportModal}
            onTermsOfUse={openTermsOfUse}
            onPrivacyPolicy={openPrivacyPolicy}
            showContactUs={effectiveShowContactUs(org)}
            showReleaseNotes={effectiveShowReleaseNotes(org)}
            showGiveFeedback={effectiveShowGiveFeedback(org)}
          />
        )}
      </Box>

      {/* Modals and dialogs */}
      <ErrorBoundary>
        <CustomerSupportModal
          isOpen={customerSupportModalOpen}
          onClose={() => setCustomerSupportModalOpen(false)}
        />
        <ContactUsModal
          isOpen={contactUSModalOpen}
          onClose={() => setContactUSModalOpen(false)}
          customHtml={resolveContactUsCustomHtmlFromOrg(org)}
        />
      </ErrorBoundary>
      <TermsOfUseDialog open={openTOU} setOpen={setOpenTOU} />
      <PrivacyPolicyDialog open={openPP} setOpen={setOpenPP} />
    </>
  );
};

export default SidebarBase;
